const stableNodeId = (question) => String(
  question?.release_node_id || question?.id || "",
);

const projectionNodeId = (projection) => String(
  projection?.current_node?.node_id
  || projection?.current_node?.release_node_id
  || projection?.current_node?.id
  || "",
);

const exactNodeIdentities = (question) => [
  question?.release_node_id,
  question?.id,
].filter(Boolean).map(String);

const exactIdentityIndex = (questions, identity) => {
  if (!identity) return -1;
  const target = String(identity);
  const matches = questions.reduce((indexes, question, index) => (
    exactNodeIdentities(question).includes(target)
      ? [...indexes, index]
      : indexes
  ), []);
  return matches.length === 1 ? matches[0] : -1;
};

const occurrenceOwnerId = (question) => String(
  question?.program_id
  || question?.section_origin?.owner_id
  || "",
);

const canonicalSourceIndex = (questions, sourceId, ownerId = null) => {
  if (!sourceId) return -1;
  const source = String(sourceId);
  const matches = questions.reduce((indexes, question, index) => {
    if (String(question?.source_id || "") !== source) return indexes;
    if (
      ownerId !== null
      && occurrenceOwnerId(question) !== String(ownerId)
    ) return indexes;
    return [...indexes, index];
  }, []);
  return matches.length === 1 ? matches[0] : -1;
};

const sourceIndexForQuestion = (questions, question) => {
  const exact = exactIdentityIndex(questions, stableNodeId(question));
  if (exact >= 0) return exact;
  return canonicalSourceIndex(
    questions,
    question?.source_id,
    occurrenceOwnerId(question),
  );
};

const sourceIndexForIdentity = (questions, identity) => {
  const exact = exactIdentityIndex(questions, identity);
  if (exact >= 0) return exact;
  // A bare canonical source ID is safe only when the immutable release has a
  // single occurrence. Reusable Section fields can legitimately repeat in
  // sibling Programs, so choosing the first match would corrupt the cursor.
  return canonicalSourceIndex(questions, identity);
};

export const resolveForwardNodeIndex = ({
  allQuestions,
  visibleQuestions,
  currentNodeId,
}) => {
  const all = Array.isArray(allQuestions) ? allQuestions : [];
  const visible = Array.isArray(visibleQuestions) ? visibleQuestions : [];
  if (!visible.length || !currentNodeId) return -1;

  const currentSourceIndex = sourceIndexForIdentity(all, currentNodeId);
  if (currentSourceIndex < 0) return -1;

  return visible.findIndex((question) => {
    const sourceIndex = sourceIndexForQuestion(all, question);
    return sourceIndex > currentSourceIndex;
  });
};

export const resolveStableNodeIndex = ({
  allQuestions,
  visibleQuestions,
  currentNodeId,
  fallbackState = null,
  stateForQuestion = () => null,
}) => {
  const all = Array.isArray(allQuestions) ? allQuestions : [];
  const visible = Array.isArray(visibleQuestions) ? visibleQuestions : [];
  if (!visible.length) return -1;

  const authoritativeStateIndex = fallbackState
    ? visible.findIndex(
      (question) => stateForQuestion(question) === fallbackState,
    )
    : -1;

  const targetId = String(currentNodeId || "");
  if (targetId) {
    const exact = visible.findIndex(
      (question) => stableNodeId(question) === targetId,
    );
    if (exact >= 0) {
      const cursorState = stateForQuestion(visible[exact]);
      if (
        authoritativeStateIndex >= 0
        && cursorState !== fallbackState
      ) {
        return authoritativeStateIndex;
      }
      return exact;
    }

    const sourceIndex = all.findIndex(
      (question) => stableNodeId(question) === targetId,
    );
    if (sourceIndex >= 0) {
      const visibleSourceIndexes = visible.map((question) => (
        all.findIndex((candidate) => stableNodeId(candidate) === stableNodeId(question))
      ));
      let nearestPrior = -1;
      let nearestPriorSourceIndex = -1;
      let nearestNext = -1;
      let nearestNextSourceIndex = Number.POSITIVE_INFINITY;
      visibleSourceIndexes.forEach((candidateSourceIndex, visibleIndex) => {
        if (candidateSourceIndex < 0) return;
        if (
          candidateSourceIndex < sourceIndex
          && candidateSourceIndex > nearestPriorSourceIndex
        ) {
          nearestPrior = visibleIndex;
          nearestPriorSourceIndex = candidateSourceIndex;
        }
        if (
          candidateSourceIndex > sourceIndex
          && candidateSourceIndex < nearestNextSourceIndex
        ) {
          nearestNext = visibleIndex;
          nearestNextSourceIndex = candidateSourceIndex;
        }
      });
      if (nearestPrior >= 0) return nearestPrior;
      if (nearestNext >= 0) return nearestNext;
    }
  }

  if (fallbackState) {
    if (authoritativeStateIndex >= 0) return authoritativeStateIndex;
    if (["checkout", "product_selection", "completed", "review"].includes(String(fallbackState))) {
      return visible.length;
    }
  }
  return 0;
};

/**
 * Resolve a UI cursor without allowing browser state to cross an authoritative
 * runtime boundary. An exact browser node is retained inside the same state so
 * partially completed screening is not rewound; otherwise the server-selected
 * released node wins.
 */
export const resolveAuthoritativeNodeIndex = ({
  allQuestions,
  visibleQuestions,
  browserNodeId = null,
  projection,
  stateForQuestion = () => null,
}) => {
  const visible = Array.isArray(visibleQuestions) ? visibleQuestions : [];
  const state = projection?.state || projection?.current_node?.state || null;
  if (projection?.presentation_contract_version === "1") {
    return resolveStableNodeIndex({
      allQuestions,
      visibleQuestions: visible,
      currentNodeId: projectionNodeId(projection),
      fallbackState: state,
      stateForQuestion,
    });
  }
  const browserIndex = exactIdentityIndex(visible, browserNodeId);
  if (
    browserIndex >= 0
    && (!state || stateForQuestion(visible[browserIndex]) === state)
  ) {
    return browserIndex;
  }

  return resolveStableNodeIndex({
    allQuestions,
    visibleQuestions: visible,
    currentNodeId: projectionNodeId(projection),
    fallbackState: state,
    stateForQuestion,
  });
};

/** Return the first visible released item owned by an authority boundary. */
export const firstVisibleNodeIndexForState = ({
  visibleQuestions,
  targetState,
  stateForQuestion = () => null,
}) => {
  const visible = Array.isArray(visibleQuestions) ? visibleQuestions : [];
  if (!targetState) return -1;
  return visible.findIndex(
    (question) => stateForQuestion(question) === targetState,
  );
};

/** Return only a state-compatible released node for the persisted resume pointer. */
export const runtimePointerNodeId = ({
  currentQuestion,
  projection,
  stateForQuestion = () => null,
}) => {
  const state = projection?.state || projection?.current_node?.state || null;
  if (projection?.presentation_contract_version === "1") {
    return projectionNodeId(projection) || null;
  }
  const currentId = stableNodeId(currentQuestion);
  if (
    currentId
    && (!state || stateForQuestion(currentQuestion) === state)
  ) {
    return currentId;
  }
  return projectionNodeId(projection) || null;
};

export const authorityCursorRequiresNavigation = ({
  currentQuestion,
  projection,
  stateForQuestion = () => null,
}) => {
  if (!currentQuestion || !projection) return false;
  const state = projection?.state || projection?.current_node?.state || null;
  if (stateForQuestion(currentQuestion) !== state) return true;
  if (projection?.presentation_contract_version !== "1") return false;
  const authoritativeNodeId = projectionNodeId(projection);
  return Boolean(
    authoritativeNodeId
    && stableNodeId(currentQuestion) !== authoritativeNodeId
  );
};

export const shouldPreservePreviewAuthenticationCursor = ({
  enabled = false,
  previewCapabilityToken = null,
  currentQuestion,
  projection,
}) => Boolean(
  enabled
  && previewCapabilityToken
  && projection?.mode === "preview"
  && projection?.patient_bound === false
  && projection?.state === "routing"
  && currentQuestion?.question_type === "patient_authentication"
);

/** Build resume navigation independently from whether the session has answers. */
export const runtimeResumeCursor = ({ resumed, savedRuntime }) => {
  if (!resumed?.patient_bound) return null;
  return {
    itemId: savedRuntime?.current_node_id || null,
    projection: resumed,
    state: resumed.state || null,
  };
};
